import { useState, useEffect } from 'react';
import { MdOutlineCheckCircle } from 'react-icons/md';
import { LuCircleEllipsis } from 'react-icons/lu';
import FinancialInfoDrawer from '../../CampaignDetail/drawers/FinancialInfoDrawer';

interface Props {
    patient?: any;
    campaignRequest?: any;
    metadata?: any;
    application?: any;
    completionStatus?: any;
    onDataSaved?: () => void;
}

const IDENTITY_CHOICES = [
    { value: 1, label: 'Client' },
    { value: 2, label: 'Spouse' },
    { value: 3, label: 'Parent of minor client' },
    { value: 4, label: 'Legal Guardian' },
    { value: 5, label: 'POA' },
];

const formatCurrency = (value: number | string | null | undefined): string => {
    if (value === null || value === undefined) return '';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num) || num === 0) return '';
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const FinancialInfoTab: React.FC<Props> = ({
    metadata,
    patient,
    campaignRequest,
    application,
    completionStatus,
    onDataSaved,
}) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [localApplication, setLocalApplication] = useState(application);

    // Update local application when prop changes
    useEffect(() => {
        setLocalApplication(application);
    }, [application]);

    const getIdentityLabel = (value: number | null | undefined) => {
        if (!value) return '';
        const choice = IDENTITY_CHOICES.find((c) => c.value === value);
        return choice?.label || '';
    };

    // Check completion status for financial section
    const financialSection = completionStatus?.sections?.find(
        (s: any) => s.key === 'financial'
    );
    const isFinancialComplete = financialSection?.is_complete ?? false;

    return (
        <>
            <div className='mb-7.5'>
                <div className='flex items-center justify-between'>
                    <div>
                        <h3
                            className={`mb-2 text-[16px]/[24px] font-medium uppercase ${
                                isFinancialComplete
                                    ? 'text-[#AFC12D]'
                                    : 'text-[#FEBF3E]'
                            }`}
                        >
                            {isFinancialComplete ? (
                                <MdOutlineCheckCircle
                                    className='me-2'
                                    size={24}
                                />
                            ) : (
                                <LuCircleEllipsis className='me-2' size={24} />
                            )}
                            {isFinancialComplete ? 'Complete' : 'Pending'}
                        </h3>
                        <h2 className='text-[22px]/[28px] font-bold'>
                            Financial Info
                        </h2>
                    </div>

                    <button
                        className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                        onClick={() => {
                            setIsDrawerOpen(true);
                        }}
                    >
                        Edit
                    </button>
                </div>
                <div className='flex justify-between gap-2'>
                    <div className='grow'>
                        <div
                            className={`mb-[16px] ${
                                localApplication?.fin_num_in_household
                                    ? ''
                                    : 'bg-[#FDEAEB] px-1'
                            }`}
                        >
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Number in household
                                <span className='align-top text-[#F02D3A]'>
                                    *
                                </span>
                            </label>
                            {localApplication?.fin_num_in_household ? (
                                <div>
                                    {localApplication?.fin_num_in_household}
                                </div>
                            ) : (
                                <div className='text-danger'>Required</div>
                            )}
                        </div>
                        <div
                            className={`mb-[16px] ${
                                localApplication?.fin_is_single_parent_household !==
                                    null &&
                                localApplication?.fin_is_single_parent_household !==
                                    undefined
                                    ? ''
                                    : 'bg-[#FDEAEB] px-1'
                            }`}
                        >
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Is this a single parent household?
                                <span className='align-top text-[#F02D3A]'>
                                    *
                                </span>
                            </label>
                            {localApplication?.fin_is_single_parent_household !==
                                null &&
                            localApplication?.fin_is_single_parent_household !==
                                undefined ? (
                                <div>
                                    {localApplication?.fin_is_single_parent_household
                                        ? 'Yes'
                                        : 'No'}
                                </div>
                            ) : (
                                <div className='text-danger'>Required</div>
                            )}
                        </div>
                        <div
                            className={`mb-[16px] ${
                                localApplication?.fin_is_irs_dependent !==
                                    null &&
                                localApplication?.fin_is_irs_dependent !==
                                    undefined
                                    ? ''
                                    : 'bg-[#FDEAEB] px-1'
                            }`}
                        >
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Does a parent, spouse or other relative claim
                                you as a dependent on their tax return?
                                <span className='align-top text-[#F02D3A]'>
                                    *
                                </span>
                            </label>
                            {localApplication?.fin_is_irs_dependent !== null &&
                            localApplication?.fin_is_irs_dependent !==
                                undefined ? (
                                <div>
                                    {localApplication?.fin_is_irs_dependent
                                        ? 'Yes'
                                        : 'No'}
                                </div>
                            ) : (
                                <div className='text-danger'>Required</div>
                            )}
                        </div>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Monthly Salary/Wages/Tips
                            </label>
                            <div>
                                {formatCurrency(localApplication?.fin_monthly_wages)}
                            </div>
                        </div>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Social Security
                            </label>
                            <div>
                                {formatCurrency(localApplication?.fin_soc_sec_income)}
                            </div>
                        </div>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Social Security Disability
                            </label>
                            <div>
                                {formatCurrency(localApplication?.fin_disability_income)}
                            </div>
                        </div>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Other Income (such as capital gains, dividends,
                                interest, rents, pensions, alimony)
                            </label>
                            <div>
                                {formatCurrency(localApplication?.fin_other_income)}
                            </div>
                        </div>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Total monthly income (sum of the above)
                            </label>
                            <div>
                                {formatCurrency(localApplication?.fin_tot_monthly_income)}
                            </div>
                        </div>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Annual household income (monthly income x 12)
                            </label>
                            <div>
                                {formatCurrency(localApplication?.fin_anl_household_income)}
                            </div>
                        </div>
                    </div>

                    <div className='grow'>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Please check all your anticipated out-of-pocket
                                medical and related expenses
                                <span className='align-top text-[#F02D3A]'>
                                    *
                                </span>
                            </label>
                            <div>
                                {localApplication?.fin_has_hospital_costs && (
                                    <div>• Hospital</div>
                                )}
                                {localApplication?.fin_has_medication_costs && (
                                    <div>• Medication</div>
                                )}
                                {localApplication?.fin_has_travel_costs && (
                                    <div>• Travel</div>
                                )}
                                {localApplication?.fin_has_phys_therapy_costs && (
                                    <div>• Physical Therapy</div>
                                )}
                                {localApplication?.fin_has_copay_costs && (
                                    <div>• Co-pay</div>
                                )}
                                {localApplication?.fin_has_home_healthcare_costs && (
                                    <div>• Home Healthcare</div>
                                )}
                                {localApplication?.fin_has_post_care_costs && (
                                    <div>• Post Care</div>
                                )}
                                {localApplication?.fin_has_living_donor_costs && (
                                    <div>• Living Donor</div>
                                )}
                                {localApplication?.fin_has_donor_search_costs && (
                                    <div>• Donor Search</div>
                                )}
                                {localApplication?.fin_has_med_equip_costs && (
                                    <div>• Medical Equipment</div>
                                )}
                                {localApplication?.fin_has_home_modification_costs && (
                                    <div>• Home Modification</div>
                                )}
                                {localApplication?.fin_has_transportation_costs && (
                                    <div>• Transportation</div>
                                )}
                                {localApplication?.fin_has_emergency_living_expenses && (
                                    <div>• Emergency Living Expenses</div>
                                )}
                            </div>
                        </div>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Other/additional comments
                            </label>
                            <div>
                                {localApplication?.fin_other_comments || ''}
                            </div>
                        </div>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                I certify that I am the:
                                <span className='align-top text-[#F02D3A]'>
                                    *
                                </span>
                            </label>
                            <div>
                                {getIdentityLabel(
                                    localApplication?.fin_certify_identity
                                )}
                            </div>
                        </div>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Name
                                <span className='align-top text-[#F02D3A]'>
                                    *
                                </span>
                            </label>
                            <div>{localApplication?.fin_signature || ''}</div>
                        </div>
                        <div className='mb-[16px]'>
                            <label className='text-[12px]/[16px] text-[#949599]'>
                                Date
                                <span className='align-top text-[#F02D3A]'>
                                    *
                                </span>
                            </label>
                            <div>
                                {localApplication?.fin_signature_date
                                    ? new Date(
                                          localApplication.fin_signature_date
                                      ).toLocaleDateString()
                                    : ''}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <FinancialInfoDrawer
                isOpen={isDrawerOpen}
                setIsOpen={setIsDrawerOpen}
                application={localApplication}
                campaignRequestId={campaignRequest?.id}
                onUpdate={(response) => {
                    console.log('Financial info updated:', response);
                    // Update the local application state with the new data
                    // The API returns the full campaign request response,
                    // so we need to extract the application data
                    if (response?.application) {
                        setLocalApplication(response.application);
                    } else if (response) {
                        // If the response structure is different, update accordingly
                        setLocalApplication((prev: any) => ({
                            ...prev,
                            ...response,
                        }));
                    }

                    // Refresh completion status
                    if (onDataSaved) {
                        onDataSaved();
                    }
                }}
            />
        </>
    );
};

export default FinancialInfoTab;
